"use client";

import { createContext, useContext, useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { updateUserLanguage } from "@/server/common/userCommon";

type LanguageContextType = {
    language: string;
    setLanguage: (lang: string) => Promise<void>;
    t: (key: string) => string;
};

const LanguageContext = createContext<LanguageContextType | undefined>(
    undefined
);

export function LanguageProvider({
    children,
    initialLanguage,
}: {
    children: React.ReactNode;
    initialLanguage: string;
}) {
    const [language, setLanguageState] = useState(initialLanguage);
    const [messages, setMessages] = useState<any>(null);
    const { data: session } = useSession();

    useEffect(() => {
        const loadMessages = async () => {
            const msgs = await import(`@/messages/${language}.json`);
            setMessages(msgs.default);
        };
        loadMessages();
    }, [language]);

    const setLanguage = async (newLang: string) => {
        try {
            if (!session?.user) {
                console.log("Not authenticated");
                setLanguageState(newLang);
                return;
                // throw new Error("Not authenticated");
            }
            await updateUserLanguage(newLang, session);
            setLanguageState(newLang);
        } catch (error) {
            console.error("Failed to update language:", error);
        }
    };

    const t = (key: string) => {
        if (!messages) return key;
        const keys = key.split(".");
        let value: any = messages;
        for (const k of keys) {
            value = value?.[k];
            if (value === undefined) return key;
        }
        return value;
    };

    return (
        <LanguageContext.Provider value={{ language, setLanguage, t }}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useLanguage(shouldThrow?: true): LanguageContextType;
export function useLanguage(
    shouldThrow: false
): LanguageContextType | undefined;
export function useLanguage(
    shouldThrow = true
): LanguageContextType | undefined {
    const context = useContext(LanguageContext);
    if (shouldThrow && !context) {
        throw new Error("useLanguage must be used within a LanguageProvider");
    }
    return context;
}
